'use client';

import React, { useState } from 'react';
import AppLayout from '@/components/AppLayout';
import {
  ArrowLeftRight, Filter, Download, Search, Eye, FileText,
  ArrowUpRight, ArrowDownLeft, DollarSign, TrendingUp, RefreshCw, AlertTriangle
} from 'lucide-react';

const formatVT = (n: number) => `VT ${n.toLocaleString('en-US')}`;

type TxType = 'Disbursement' | 'Repayment' | 'Capital Contribution' | 'Fee Collection' | 'Withdrawal' | 'Expense';

const transactions = [
  { id: 'TXN-001', date: '2026-07-28', type: 'Repayment' as TxType, description: 'Loan VL-2026-001 — Kalani Moli', amount: 80000, direction: 'in', ref: 'RCP-001', staff: 'Siosaia Taufa' },
  { id: 'TXN-002', date: '2026-07-24', type: 'Repayment' as TxType, description: 'Loan VL-2026-002 — Tina Bule', amount: 210000, direction: 'in', ref: 'RCP-002', staff: 'Siosaia Taufa' },
  { id: 'TXN-003', date: '2026-07-20', type: 'Capital Contribution' as TxType, description: 'Investor: Siosaia Taufa — Q3 injection', amount: 500000, direction: 'in', ref: 'CAP-001', staff: 'Siosaia Taufa' },
  { id: 'TXN-004', date: '2026-07-20', type: 'Disbursement' as TxType, description: 'Loan VL-2026-003 — Rex Naupa', amount: 150000, direction: 'out', ref: 'VL-2026-003', staff: 'Siosaia Taufa' },
  { id: 'TXN-005', date: '2026-07-15', type: 'Capital Contribution' as TxType, description: 'Investor: Mere Vanuatu — Additional capital', amount: 300000, direction: 'in', ref: 'CAP-002', staff: 'Siosaia Taufa' },
  { id: 'TXN-006', date: '2026-07-10', type: 'Repayment' as TxType, description: 'Loan VL-2026-004 — Sam Kaltonga', amount: 100000, direction: 'in', ref: 'RCP-003', staff: 'Siosaia Taufa' },
  { id: 'TXN-007', date: '2026-07-10', type: 'Fee Collection' as TxType, description: 'Late fee — VL-2026-004 Sam Kaltonga', amount: 23600, direction: 'in', ref: 'FEE-001', staff: 'Siosaia Taufa' },
  { id: 'TXN-008', date: '2026-07-05', type: 'Disbursement' as TxType, description: 'Loan VL-2026-005 — Grace Vira (pending)', amount: 500000, direction: 'out', ref: 'VL-2026-005', staff: 'Siosaia Taufa' },
  { id: 'TXN-009', date: '2026-07-01', type: 'Withdrawal' as TxType, description: 'Investor withdrawal — John Kalsakau', amount: 200000, direction: 'out', ref: 'WDR-001', staff: 'Siosaia Taufa' },
  { id: 'TXN-010', date: '2026-06-30', type: 'Expense' as TxType, description: 'Office supplies & admin costs', amount: 15000, direction: 'out', ref: 'EXP-001', staff: 'Siosaia Taufa' },
];

const typeConfig: Record<TxType, { icon: React.ReactNode; color: string; bg: string }> = {
  Disbursement: { icon: <ArrowUpRight size={14} />, color: 'text-negative', bg: 'bg-negative/10' },
  Repayment: { icon: <RefreshCw size={14} />, color: 'text-positive', bg: 'bg-positive/10' },
  'Capital Contribution': { icon: <TrendingUp size={14} />, color: 'text-info', bg: 'bg-info/10' },
  'Fee Collection': { icon: <DollarSign size={14} />, color: 'text-warning', bg: 'bg-warning/10' },
  Withdrawal: { icon: <ArrowDownLeft size={14} />, color: 'text-orange-400', bg: 'bg-orange-400/10' },
  Expense: { icon: <AlertTriangle size={14} />, color: 'text-muted-foreground', bg: 'bg-muted' },
};

const ALL_TYPES: TxType[] = ['Disbursement', 'Repayment', 'Capital Contribution', 'Fee Collection', 'Withdrawal', 'Expense'];

export default function TransactionsPage() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TxType | 'All'>('All');
  const [selectedTx, setSelectedTx] = useState<typeof transactions[0] | null>(null);

  const filtered = transactions.filter(t => {
    const matchSearch = t.description.toLowerCase().includes(search.toLowerCase()) || t.id.includes(search) || t.ref.includes(search);
    const matchType = typeFilter === 'All' || t.type === typeFilter;
    return matchSearch && matchType;
  });

  const totalIn = transactions.filter(t => t.direction === 'in').reduce((s, t) => s + t.amount, 0);
  const totalOut = transactions.filter(t => t.direction === 'out').reduce((s, t) => s + t.amount, 0);
  const netFlow = totalIn - totalOut;

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Transactions</h1>
            <p className="text-sm text-muted-foreground mt-0.5">Master general ledger — all monetary movements with full audit trail</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Filter size={16} /> Filter Transactions
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <Download size={16} /> Export CSV
            </button>
            <button className="flex items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border text-sm font-medium text-foreground hover:bg-muted transition-colors">
              <FileText size={16} /> Export PDF
            </button>
          </div>
        </div>

        {/* Cash Flow Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-card border border-positive/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-positive/10 flex items-center justify-center"><ArrowDownLeft size={16} className="text-positive" /></div>
              <span className="text-xs text-muted-foreground font-medium">Total Inflows</span>
            </div>
            <p className="text-xl font-bold text-positive font-mono">{formatVT(totalIn)}</p>
            <p className="text-xs text-muted-foreground mt-1">{transactions.filter(t => t.direction === 'in').length} transactions</p>
          </div>
          <div className="bg-card border border-negative/20 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 rounded-lg bg-negative/10 flex items-center justify-center"><ArrowUpRight size={16} className="text-negative" /></div>
              <span className="text-xs text-muted-foreground font-medium">Total Outflows</span>
            </div>
            <p className="text-xl font-bold text-negative font-mono">{formatVT(totalOut)}</p>
            <p className="text-xs text-muted-foreground mt-1">{transactions.filter(t => t.direction === 'out').length} transactions</p>
          </div>
          <div className={`bg-card border rounded-xl p-4 ${netFlow >= 0 ? 'border-positive/20' : 'border-negative/20'}`}>
            <div className="flex items-center gap-2 mb-2">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${netFlow >= 0 ? 'bg-positive/10' : 'bg-negative/10'}`}>
                <ArrowLeftRight size={16} className={netFlow >= 0 ? 'text-positive' : 'text-negative'} />
              </div>
              <span className="text-xs text-muted-foreground font-medium">Net Cash Flow</span>
            </div>
            <p className={`text-xl font-bold font-mono ${netFlow >= 0 ? 'text-positive' : 'text-negative'}`}>{netFlow >= 0 ? '+' : ''}{formatVT(netFlow)}</p>
            <p className="text-xs text-muted-foreground mt-1">Period net position</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search ledger..." className="w-full pl-9 pr-4 py-2 rounded-lg bg-card border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary" />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button onClick={() => setTypeFilter('All')} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${typeFilter === 'All' ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}>All</button>
            {ALL_TYPES.map(t => (
              <button key={t} onClick={() => setTypeFilter(t)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${typeFilter === t ? 'bg-primary/10 text-primary border border-primary/30' : 'bg-card border border-border text-muted-foreground hover:text-foreground'}`}>{t}</button>
            ))}
          </div>
        </div>

        {/* Ledger Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Txn ID</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Description</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Reference</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Amount</th>
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filtered.map(tx => {
                  const cfg = typeConfig[tx.type];
                  return (
                    <tr key={tx.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{tx.id}</td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">{tx.date}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.color}`}>
                          {cfg.icon} {tx.type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-foreground max-w-xs truncate">{tx.description}</td>
                      <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{tx.ref}</td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-mono font-bold text-sm ${tx.direction === 'in' ? 'text-positive' : 'text-negative'}`}>
                          {tx.direction === 'in' ? '+' : '-'}{formatVT(tx.amount)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => setSelectedTx(selectedTx?.id === tx.id ? null : tx)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title="View Details">
                          <Eye size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Transaction Detail Panel */}
        {selectedTx && (
          <div className="bg-card border border-primary/20 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-foreground">Transaction Details — {selectedTx.id}</h3>
              <button onClick={() => setSelectedTx(null)} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
                <Eye size={14} />
              </button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                ['Transaction ID', selectedTx.id],
                ['Date', selectedTx.date],
                ['Type', selectedTx.type],
                ['Direction', selectedTx.direction === 'in' ? '↓ Inflow' : '↑ Outflow'],
                ['Amount', `${selectedTx.direction === 'in' ? '+' : '-'}${formatVT(selectedTx.amount)}`],
                ['Reference', selectedTx.ref],
                ['Staff', selectedTx.staff],
                ['Description', selectedTx.description],
              ].map(([k, v]) => (
                <div key={k} className="p-3 rounded-lg bg-muted/30">
                  <p className="text-xs text-muted-foreground mb-0.5">{k}</p>
                  <p className="text-sm font-medium text-foreground truncate">{v}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
